// Widget type configurations
export const WIDGET_TYPES = {
  EVENT_LOG: 'eventLog',
  EVENTS: 'events',
  GAUGE: 'gauge',
  MAP: 'map',
  STATS_CARD: 'statsCard',
  STATUS: 'status',
  TABLE: 'table',
  TIMESERIES: 'timeseries'
};

// Widget metadata for UI display
export const widgetRegistry = [
  {
    type: WIDGET_TYPES.EVENTS,
    label: 'Events',
    description: 'Display event log table',
    icon: '📅',
    defaultSize: { w: 12, h: 6 }
  },
  {
    type: WIDGET_TYPES.GAUGE,
    label: 'Gauge',
    description: 'Display a single metric with visual gauge',
    icon: '⚡',
    defaultSize: { w: 4, h: 6 }
  },
  {
    type: WIDGET_TYPES.MAP,
    label: 'Map',
    description: 'Display location data on a map',
    icon: '🗺️',
    defaultSize: { w: 6, h: 8 }
  },
  {
    type: WIDGET_TYPES.EVENT_LOG,
    label: 'Message Log',
    description: 'Display a live message log with timestamps and messages',
    icon: '📋',
    defaultSize: { w: 6, h: 6 }
  },
  {
    type: WIDGET_TYPES.STATS_CARD,
    label: 'Stats Card',
    description: 'Show multiple statistics in a card',
    icon: '📊',
    defaultSize: { w: 4, h: 4 }
  },
  {
    type: WIDGET_TYPES.STATUS,
    label: 'Status',
    description: 'Display a boolean value as an on/off indicator',
    icon: '🔘',
    defaultSize: { w: 3, h: 4 }
  },
  {
    type: WIDGET_TYPES.TABLE,
    label: 'Table',
    description: 'Show data in a table format',
    icon: '📋',
    defaultSize: { w: 6, h: 6 }
  },
  {
    type: WIDGET_TYPES.TIMESERIES,
    label: 'Time Series',
    description: 'Plot data over time',
    icon: '📈',
    defaultSize: { w: 12, h: 8 }
  }
];

// Get widget metadata by type
export function getWidgetMetadata(type) {
  return widgetRegistry.find(w => w.type === type);
}

// Get default configuration for a widget type
export function getDefaultWidgetConfig(type) {
  const metadata = getWidgetMetadata(type);
  if (!metadata) return null;

  const config = {
    type,
    title: '',
    gridWidth: metadata.defaultSize.w,
    gridHeight: metadata.defaultSize.h,
    config: {}
  };

  // Add type-specific defaults
  switch (type) {
    case WIDGET_TYPES.EVENT_LOG:
      config.config = {
        timeFormat: '24hour'
      };
      break;
    case WIDGET_TYPES.GAUGE:
      config.config = {
        source: '',
        field: '',
        unit: '',
        valuePrecision: '.2f',
        range: [0, 100],
        color: '#000000',
        bgColor: 'white',
        steps: []
      };
      break;
    case WIDGET_TYPES.STATS_CARD:
      config.config = {
        source: '',
        specifications: {}
      };
      break;
    case WIDGET_TYPES.MAP:
      config.config = {
        source: 'gpgga_Internal',
        latitudeField: 'lat',
        longitudeField: 'lon'
      };
      break;
    case WIDGET_TYPES.STATUS:
      config.config = {
        source: '',
        field: '',
        mode: 'toggle',
        onLabel: '',
        offLabel: '',
        states: []
      };
      break;
    case WIDGET_TYPES.TABLE:
      config.config = {
        source: '',
        columns: [],
        rowLimit: 10
      };
      break;
    case WIDGET_TYPES.TIMESERIES:
      config.config = {
        specifications: {}
      };
      break;
    case WIDGET_TYPES.EVENTS:
      config.config = {
        columns: ["startUTC", "endUTC", "duration", "tag"],
        source: '',
        title: '',
        comparison: '',
        startDate: '',
        values: [{source: '', field: ''}]
      };
      break;
  }

  return config;
}

// Validate widget configuration
export function validateWidgetConfig(type, config, title) {
  if (!title || !title.trim()) return false;

  switch (type) {
    case WIDGET_TYPES.EVENT_LOG:
      return true;
    case WIDGET_TYPES.GAUGE:
      return config.source && config.field;
    case WIDGET_TYPES.STATS_CARD: {
      // Require source
      if (!config.source) return false;
      
      // Require at least one specification with a field selected
      const specs = config.specifications?.[config.source] || [];
      return specs.length > 0 && specs.some(spec => spec.field);
    }
    case WIDGET_TYPES.MAP:
      return !!config.source;
    case WIDGET_TYPES.STATUS:
      return !!(config.source && config.field);
    case WIDGET_TYPES.TABLE: {
      // Require source
      if (!config.source) return false;
      
      // Require at least one column with a field selected
      const columns = config.columns || [];
      return columns.length > 0 && columns.some(col => col.field);
    }
    case WIDGET_TYPES.TIMESERIES: {
      // Require at least one specification with a field selected
      const specs = Object.values(config.specifications || {}).flat();
      return specs.length > 0 && specs.some(spec => spec.field);
    }
    case WIDGET_TYPES.EVENTS: {
      // Validate title
      if (!title || title.trim() === '') {
        return false;
      }

      // Validate config fields
      if (!config.title || config.title.trim() === '') {
        return false;
      }

      if (!config.comparison || config.comparison.trim() === '') {
        return false;
      }

      if (!config.startDate || config.startDate.trim() === '') {
        return false;
      }

      if (!config.values || config.values.length === 0) {
        return false;
      }

      // Validate all variables have source and field
      for (const variable of config.values) {
        if (!variable.source || !variable.field) {
          return false;
        }
      }

      return true;
    }
    default:
      return true;
  }
}
